import { applyStageNames } from '@/api/companySettingsApi'
import { ApiError } from '@/api/errors'
import type { LoginInput } from '@/features/auth/schemas/authSchemas'
import type { RoleOption, Session } from '@/features/auth/types'
import {
  DEMO_STAGES,
  demoSettings,
  demoStageNames,
} from '@/features/settings/demo/demoCompanySettings'
import { todayIn } from '@/lib/utils/dates'

export const DEMO_CREDENTIALS = {
  email: 'admin@ail-demo.local',
  password: 'demo123',
} as const

/** The platform admin: signs in at no Standort and keeps the Ablefy catalogue. */
export const DEMO_PLATFORM_CREDENTIALS = {
  email: 'plattform@ail-demo.local',
  password: 'demo123',
} as const

/** The company admin: signs in to manage the workspace's brand and terms. */
export const DEMO_ADMIN_CREDENTIALS = {
  email: 'unternehmen@ail-demo.local',
  password: 'demo123',
} as const

/** The franchise partner of "Franchise Süd": signs in to its own Verwaltung. */
export const DEMO_FRANCHISE_CREDENTIALS = {
  email: 'franchise@ail-demo.local',
  password: 'demo123',
} as const

/** The origins the real backend enumerates, so the demo offers the same list. */
export const DEMO_ORIGINS = [
  { origin: 'website', originLabel: 'Website' },
  { origin: 'referral', originLabel: 'Empfehlung' },
  { origin: 'social_media', originLabel: 'Social Media' },
]

/** What `GET /api/invitations/roles` answers in the seed, in the backend's order. */
export const DEMO_INVITABLE_ROLES: RoleOption[] = [
  { key: 'branch_staff', label: 'Mitarbeiter', tier: 'location' },
  { key: 'branch_manager', label: 'Filialleiter', tier: 'location' },
]

/** Head office also invites franchise partners, at a franchise (cascading-rights plan, Task 8). */
export const DEMO_HEAD_OFFICE_INVITABLE_ROLES: RoleOption[] = [
  { key: 'franchise_partner', label: 'Franchisenehmer', tier: 'franchise' },
  ...DEMO_INVITABLE_ROLES,
]

const LOCATIONS: Session['locations'] = [
  {
    publicId: '00000000-0000-0000-0000-000000000101',
    name: 'Studio München',
    timezone: 'Europe/Berlin',
  },
  {
    publicId: '00000000-0000-0000-0000-000000000102',
    name: 'Studio New York',
    timezone: 'America/New_York',
  },
]

/**
 * Franchise Süd's Standorte. Literals mirroring `features/admin/demo/demoAdmin.ts`
 * (`DEMO_ADMIN_IDS.location*`) rather than an import: that module reaches back
 * into this one through the invitations demo.
 */
const FRANCHISE_LOCATIONS: Session['locations'] = [
  {
    publicId: '00000000-0000-0000-0000-000000000401',
    name: 'Stuttgart Fellbach',
    timezone: 'Europe/Berlin',
  },
  {
    publicId: '00000000-0000-0000-0000-000000000402',
    name: 'Ludwigsburg',
    timezone: 'Europe/Berlin',
  },
  {
    publicId: '00000000-0000-0000-0000-000000000403',
    name: 'Esslingen',
    timezone: 'Europe/Berlin',
  },
]

/** `DEMO_FRANCHISE_PARTNER_ID` in `demoAdmin.ts`, so the partner finds their own row. */
const DEMO_FRANCHISE_USER: Session['user'] = {
  publicId: '00000000-0000-0000-0000-000000000201',
  displayName: 'Julia Franchise',
  email: DEMO_FRANCHISE_CREDENTIALS.email,
}

const DEMO_USER: Session['user'] = {
  publicId: '00000000-0000-0000-0000-0000000000001',
  displayName: 'Admin',
  email: DEMO_CREDENTIALS.email,
}

/** The company admin gets its own identity — it is a different seat, not the branch admin wearing another hat. */
const DEMO_ADMIN_USER: Session['user'] = {
  publicId: '00000000-0000-0000-0000-000000000002',
  displayName: 'Unternehmensadmin',
  email: DEMO_ADMIN_CREDENTIALS.email,
}

/**
 * The session skeleton for both demo studio logins. `workspace` and `stages`
 * are intentionally absent here — they are the company admin's saved state,
 * so every return point below recomputes them fresh from
 * `@/features/settings/demo/demoCompanySettings` (`demoSettings`,
 * `demoStageNames`) rather than keeping a second, stale copy.
 */
const DEMO_SESSION_BASE: Omit<Session, 'workspace' | 'stages' | 'today'> = {
  user: DEMO_USER,
  role: { key: 'branch_manager', label: 'Filialleiter' },
  location: LOCATIONS[0],
  origins: DEMO_ORIGINS,
  locations: LOCATIONS,
}

/**
 * What the backend answers a `system_admin`: no Standort, no today, empty
 * lists. It still carries a `workspace` — the type requires one, and the
 * platform shell reads brand/terms the same as any other session.
 */
const DEMO_PLATFORM_SESSION_BASE: Omit<Session, 'workspace' | 'stages' | 'today'> = {
  user: {
    publicId: '00000000-0000-0000-0000-0000000000009',
    displayName: 'Paula Plattform',
    email: DEMO_PLATFORM_CREDENTIALS.email,
  },
  role: { key: 'system_admin', label: 'Plattformbetreiber' },
  location: null,
  origins: [],
  locations: [],
}

/** The board columns, renamed with whatever the company admin has saved so far. */
function currentStages(): Session['stages'] {
  return applyStageNames(DEMO_STAGES, demoStageNames(DEMO_STAGES))
}

/**
 * The signed-in demo session, so a Standort switch (and, in `login`, a
 * re-sign-in) can carry the current user's role and identity forward instead
 * of resetting to the plain studio login. Module-level rather than read from
 * `useAuthStore`: `api/` does not otherwise depend on `stores/`, and this
 * keeps the demo fixture self-contained.
 */
let currentDemoSession: Session | null = null

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

function matches(input: LoginInput, credentials: { email: string; password: string }): boolean {
  return (
    input.email.trim().toLowerCase() === credentials.email &&
    input.password === credentials.password
  )
}

/**
 * The signed-in demo user's id, for demo flows that need to know "is this me"
 * (staff block/unblock) without importing a store. `features/admin/demo/demoAdmin.ts`
 * reads this instead of importing this module directly, to keep that one-way:
 * admin fixtures must never import `demoAuth` (import-cycle risk).
 */
export function currentDemoUserId(): string | null {
  return currentDemoSession?.user.publicId ?? null
}

/** The signed-in demo role, so demo answers can depend on the caller's tier as the server's do. */
export function currentDemoRoleKey(): string | null {
  return currentDemoSession?.role.key ?? null
}

export const demoAuth = {
  login: async (input: LoginInput): Promise<Session> => {
    await wait(450)

    if (matches(input, DEMO_PLATFORM_CREDENTIALS)) {
      // Recomputed on every sign-in: "today" must not go stale overnight.
      currentDemoSession = {
        ...DEMO_PLATFORM_SESSION_BASE,
        today: null,
        stages: [],
        workspace: demoSettings().effective,
      }
      return currentDemoSession
    }

    if (matches(input, DEMO_ADMIN_CREDENTIALS)) {
      currentDemoSession = {
        ...DEMO_SESSION_BASE,
        user: DEMO_ADMIN_USER,
        role: { key: 'company_admin', label: 'Unternehmensadmin' },
        today: todayIn(LOCATIONS[0].timezone),
        stages: currentStages(),
        workspace: demoSettings().effective,
      }
      return currentDemoSession
    }

    if (matches(input, DEMO_FRANCHISE_CREDENTIALS)) {
      currentDemoSession = {
        ...DEMO_SESSION_BASE,
        user: DEMO_FRANCHISE_USER,
        role: { key: 'franchise_partner', label: 'Franchisenehmer' },
        location: FRANCHISE_LOCATIONS[0],
        locations: FRANCHISE_LOCATIONS,
        today: todayIn(FRANCHISE_LOCATIONS[0].timezone),
        stages: currentStages(),
        workspace: demoSettings().effective,
      }
      return currentDemoSession
    }

    if (!matches(input, DEMO_CREDENTIALS)) {
      throw new ApiError({
        message: 'E-Mail oder Passwort ist nicht korrekt.',
        status: 401,
        code: 'INVALID_CREDENTIALS',
      })
    }

    currentDemoSession = {
      ...DEMO_SESSION_BASE,
      today: todayIn(LOCATIONS[0].timezone),
      stages: currentStages(),
      workspace: demoSettings().effective,
    }
    return currentDemoSession
  },

  switchLocation: async (location: string): Promise<Session> => {
    await wait(300)
    // A Standort this session reaches — the franchise partner's are not the studio login's.
    const reachable = currentDemoSession?.locations.length
      ? currentDemoSession.locations
      : LOCATIONS
    const chosen = reachable.find((candidate) => candidate.publicId === location)
    if (!chosen) {
      throw new ApiError({
        message: 'Diesem Standort sind Sie nicht zugeordnet.',
        status: 422,
        code: 'VALIDATION_FAILED',
        fieldErrors: { location: 'not assigned' },
      })
    }
    // Carry the signed-in user's identity (role included) forward, rather than
    // resetting to the plain studio login — only the location (and the state
    // it feeds) changes.
    const base = currentDemoSession ?? {
      ...DEMO_SESSION_BASE,
      stages: [],
      workspace: demoSettings().effective,
    }
    currentDemoSession = {
      ...base,
      location: chosen,
      today: todayIn(chosen.timezone),
      stages: currentStages(),
      workspace: demoSettings().effective,
    }
    return currentDemoSession
  },

  logout: async (): Promise<void> => {
    await wait(120)
    currentDemoSession = null
  },
}
